/**
 * career-values-interview — the qualitative Werte-Interview (W1 redesign §4–§5,
 * V3/V4), route `/workshops/values/interview`.
 *
 * Phase 2 (Runden-Planner) + Phase 3a (adaptive interview) that continue AFTER the
 * Werte-Kartenspiel: the planner reads the committed pyramid and plans 2–3 rounds of
 * question blocks; the interview walks them through the existing probe/fold path,
 * one adaptive question at a time. HITL: the answer is composed on screen and only
 * folded on »Bestätigen« (answer-before-fold). Candidate-facing, elicited material is
 * »Signale«, NEVER »Belege«.
 *
 * PRESENTATION (stacked-timeline redesign, operator brief 2026-07-25) — ONE reading
 * column (max 34rem), no side rails:
 * - the questions themselves are the timeline nodes, grouped under their round;
 * - `answered` collapses to its title + the candidate's OWN answer (+ »Frage ansehen«
 *   and »ändern«), `current` is the ONLY expanded node, `upcoming` is one quiet line;
 * - the full question stays REVISITABLE on an answered node (operator ruling 2026-07-25):
 *   »Frage ansehen« discloses the framing, scenario, prompt and option set with the pick
 *   marked — read-only. Changing an answer is still exclusively »ändern«;
 * - the spine (marker + connector) is the vocabulary the removed plan rail used.
 * Orientation (»Interview · Runde 2 von 3«) belongs to `.shell-nav`, which already
 * carries it — this surface must NOT duplicate it. The »Signale« coverage readout
 * belonged to a right pane that no longer exists: it is PARKED in
 * `app/lib/interview-readout.ts` and deliberately not rendered here.
 *
 * PUSH-FIRST (operator finding 2026-07-30) — the surface is NOT a request/response
 * pair any more. It claims the workshop stream and re-reads the server view whenever
 * the stream moves, so the state a long AI call produces lands in EVERY open client,
 * and in the client whose own request died on the way back (mobile timeout, sleeping
 * tab, a proxy dropping a 100s POST). Two rules make that honest:
 *   - the PENDING state derives from the VIEW (`roundUngenerated`), never from the
 *     in-flight fetch — so a pushed refresh that materializes the cards resolves the
 *     spinner even though no POST ever returned;
 *   - a failed POST whose round is still ungenerated is reported as »die Fragen
 *     werden noch erstellt«, not as a dead red error.
 * The server projection stays the ONE truth: nothing about the interview is folded
 * client-side, the store touch only says »this workshop moved, read it again«.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
  type Urn,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import {
  semanticDifferential,
  readDifferentialValue,
  openTextField,
  readOpenText,
  readWhy,
  renderCardInputs,
  renderCardReview,
  isCardDegraded,
  isAnswerable,
  emptySelection,
  emptyReview,
  pairwisePoles,
  type AnswerCard,
  type AnswerOption,
  type AnswerReview,
  type AnswerSelection,
} from "../lib/elicitation-card"
import { schwartzWheel } from "../lib/schwartz-wheel"
import type { DimensionReadout } from "../lib/interview-readout"
import type { ElicitationCard, RoundPlan, SchwartzProfile, ValuePyramid } from "@career/definition-v2"
import { careerStream, workshopUrn, WORKSHOP_AGGREGATE, type ShellSession, type WorkshopStore } from "../domain"
import { acquireStreams, onChange } from "../lib/push-streams"
import { I18nToken, PushToken, RouterToken, SessionToken, WorkshopStoreToken, apiGet, apiPost, isPaymentRequired } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import { nextStep } from "../lib/next-step"
import { spineDecl, toNextStep } from "../golden-path"

// ─── The interview view the api returns (contract `InterviewView`) ──────────────

interface Turn {
  answer: { kind: string; text?: string; choice?: string; [k: string]: unknown }
  occurredAt: string
}
interface BlockView {
  block: {
    id: string
    title: string
    primitive: ElicitationCard["primitive"]
    construct: string
    targetCardIds: string[]
    framework: "schwartz" | "big_five"
    dimension: string
  }
  round: number
  roundTitle: string
  gate: "locked" | "available" | "in_progress" | "accepted"
  card: ElicitationCard | null
  skipped: boolean
  rationale: string | null
  denkweise: string | null
  turns: Turn[]
  evidence: { summary: string }[]
  producedClaimRefs: string[]
}
interface RoundView {
  round: number
  title: string
  blocks: BlockView[]
}
interface PlanRevisionNote {
  afterRound: number
  reason: string
}
interface InterviewView {
  plan: RoundPlan | null
  pyramid: ValuePyramid | null
  rounds: RoundView[]
  readouts: unknown[]
  labels: DimensionReadout[]
  revisions: PlanRevisionNote[]
  schwartzProfile: SchwartzProfile | null
  complete: boolean
}

/** Where a question sits on the stack — exactly one node is `current` (expanded). */
export type NodeState = "done" | "current" | "upcoming"

// ─── Reading an answered block back in the CANDIDATE'S own terms ────────────────
// A collapsed node shows what the person actually answered — the option THEY picked,
// their own sentence — never the model's folded »Signal« (that reads as the machine
// talking back). The option label is resolved off the block's own card.

const optionsOf = (card: ElicitationCard | null): AnswerOption[] =>
  card ? ((card as AnswerCard).options ?? []) : []

const labelOf = (card: ElicitationCard | null, id: unknown): string | null =>
  optionsOf(card).find((o) => o.id === id)?.label ?? null

/** The candidate's last answer to a block, rendered as plain German. */
export function answerOf(block: BlockView): string | null {
  const turn = block.turns[block.turns.length - 1]
  if (!turn) return null
  const { answer } = turn
  const card = block.card
  switch (answer.kind) {
    case "pairwise": {
      const poles = card ? pairwisePoles(card as AnswerCard) : []
      const pole = answer.choice === "A" ? poles[0] : answer.choice === "B" ? poles[1] : undefined
      return pole?.label ?? null
    }
    case "best_worst": {
      const best = labelOf(card, answer["best"])
      const worst = labelOf(card, answer["worst"])
      return best && worst ? `${best} — am wenigsten: ${worst}` : (best ?? worst)
    }
    case "vignette":
    case "simulation":
      return labelOf(card, answer.choice)
    case "semantic_differential": {
      const axis = card ? (card as AnswerCard).axis : undefined
      const p = typeof answer["position"] === "number" ? (answer["position"] as number) : 0.5
      if (!axis) return null
      if (p < 0.45) return axis.left
      if (p > 0.55) return axis.right
      return `${axis.left} ↔ ${axis.right}`
    }
    default:
      return typeof answer.text === "string" && answer.text.length > 0 ? answer.text : null
  }
}

/**
 * The candidate's last answer as a SELECTION, so the answered card can be re-rendered as
 * the question it was (see `renderCardReview`). The wire shape per primitive is the one
 * `dispatchAnswer` writes; vetoes arrive as `{ id, intensity }` objects.
 */
export function reviewOf(block: BlockView): AnswerReview {
  const answer = block.turns[block.turns.length - 1]?.answer
  if (!answer) return emptyReview()
  const str = (v: unknown): string | null => (typeof v === "string" && v.length > 0 ? v : null)
  const vetoes = Array.isArray(answer["vetoes"])
    ? (answer["vetoes"] as unknown[]).map((v) => (typeof v === "string" ? v : str((v as { id?: unknown })?.id))).filter((v): v is string => v !== null)
    : []
  return {
    ...emptyReview(),
    choiceId: str(answer.choice),
    bestId: str(answer["best"]),
    worstId: str(answer["worst"]),
    vetoes,
    position: typeof answer["position"] === "number" ? (answer["position"] as number) : null,
  }
}

/** The candidate's own »warum« disclosure on a block, when they wrote one. */
export function whyOf(block: BlockView): string | null {
  const why = block.turns[block.turns.length - 1]?.answer["why"]
  return typeof why === "string" && why.length > 0 ? why : null
}

export class ValuesInterviewVm implements ViewModel {
  readonly view: Signal<InterviewView | null> = signal<InterviewView | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  /**
   * Why THIS block's confirm did not go through, rendered at the block itself.
   *
   * The surface-level `error` sits under the page title, which is off-screen the moment the
   * candidate has scrolled down to the question they are answering — a failed »Bestätigen«
   * then reads as a dead button. A refusal belongs next to the control that was pressed.
   */
  readonly blockError: Signal<{ blockId: string; message: string } | null> = signal<{ blockId: string; message: string } | null>(null)
  /**
   * The work is still running even though OUR request is not — see `failPending`.
   * A quiet German status line, never an alarm; the push subscription clears it.
   */
  readonly pending: Signal<string | null> = signal<string | null>(null)
  /** A per-block confirm (or the plan) is in flight. TRANSPORT state only: what the
   *  candidate sees pending is derived from the view, not from this. */
  readonly busy: Signal<boolean> = signal(false)
  /** A whole round's stimuli are generating — the honest »thinking« state between rounds. */
  readonly roundGen: Signal<boolean> = signal(false)
  /** In-progress selections, keyed by block id. Free-text and slider values are read from the
   *  DOM on submit (no per-keystroke re-render). */
  readonly selections: Signal<Record<string, AnswerSelection>> = signal<Record<string, AnswerSelection>>({})
  /** An ALREADY-answered block the candidate re-opened via »ändern«. It becomes the one
   *  expanded node instead of the frontier; a new turn is appended (the api re-folds the
   *  block's evidence from all its turns), and confirming clears this. */
  readonly reopened: Signal<string | null> = signal<string | null>(null)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(
    private readonly router: Router,
    i18n: CareerI18n,
    private readonly push: PushChannel,
    private readonly store: WorkshopStore,
    private readonly session: ShellSession,
  ) {
    this.t = i18n.t
    this.renderDeps = [this.view, this.loaded, this.error, this.pending, this.blockError, this.busy, this.roundGen, this.selections, this.reopened]
  }

  /**
   * Read once, then LIVE: claim the self-knowledge workshop stream and re-read the
   * server view every time it moves. The re-read is the same cheap unmetered GET the
   * surface already used — it never generates anything (`maybeGenerateRound` stays on
   * the mount/answer paths ONLY, or a pushed event would spend credits by itself).
   */
  attach(): () => void {
    void this.refresh().then(() => this.maybeGenerateRound())
    const userId = this.session.user?.sub
    if (!userId) return () => {}
    const release = acquireStreams(this.push, [careerStream(userId, WORKSHOP_AGGREGATE, workshopUrn(userId, "self_knowledge"))])
    const stop = onChange(this.store.entity(workshopUrn(userId, "self_knowledge")), () => void this.refresh())
    return () => {
      stop()
      release()
    }
  }

  selFor(blockId: string): AnswerSelection {
    return this.selections.value[blockId] ?? emptySelection()
  }
  private setSel(blockId: string, sel: AnswerSelection): void {
    this.selections.set({ ...this.selections.value, [blockId]: sel })
  }
  private clearSel(blockId: string): void {
    const next = { ...this.selections.value }
    delete next[blockId]
    this.selections.set(next)
  }

  pickOption(blockId: string, role: "choice" | "best" | "worst", id: string): void {
    const s = this.selFor(blockId)
    if (role === "choice") this.setSel(blockId, { ...s, choiceId: s.choiceId === id ? null : id })
    else if (role === "best") this.setSel(blockId, { ...s, bestId: s.bestId === id ? null : id, worstId: s.worstId === id ? null : s.worstId })
    else this.setSel(blockId, { ...s, worstId: s.worstId === id ? null : id, bestId: s.bestId === id ? null : s.bestId })
  }
  toggleVeto(blockId: string, id: string): void {
    const s = this.selFor(blockId)
    const vetoes = s.vetoes.includes(id) ? s.vetoes.filter((v) => v !== id) : [...s.vetoes, id]
    this.setSel(blockId, { ...s, vetoes })
  }

  private async refresh(): Promise<void> {
    try {
      this.view.set(await apiGet<InterviewView>("/api/workshops/values/interview"))
      this.syncPending()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  /**
   * A failed POST for work that may still be RUNNING server-side.
   *
   * Round generation and planning are long AI calls; the response dies far more often
   * than the work does (a sleeping tab, a mobile network, a proxy giving up on a 100s
   * POST). The server keeps appending to the workshop stream regardless, and this
   * surface is subscribed to it — so as long as the thing we asked for has not yet
   * appeared in the view, the truthful report is »it is still being made«, not a red
   * error on a request nobody can retry meaningfully.
   *
   * A payment/entitlement block is the one refusal that will NEVER resolve by itself:
   * it stays a real error, or the candidate would wait for questions that are not
   * coming.
   */
  private failPending(e: unknown, stillOutstanding: boolean): void {
    if (stillOutstanding && !isPaymentRequired(e)) {
      this.pending.set(this.t("interview.round.stillWorking"))
      return
    }
    this.error.set(e instanceof Error ? e.message : String(e))
  }

  /** Retire the pending line as soon as the view shows the work landed. */
  private syncPending(): void {
    if (this.pending.value === null) return
    const view = this.view.value
    if (!view?.plan) return
    const round = this.currentRound()
    if (!round || !this.roundUngenerated(round)) this.pending.set(null)
  }

  allBlocks(): BlockView[] {
    return this.view.value?.rounds.flatMap((r) => r.blocks) ?? []
  }

  /** The active round — the first round holding a non-accepted block (null when complete). */
  currentRound(): RoundView | null {
    return this.view.value?.rounds.find((r) => r.blocks.some((b) => b.gate !== "accepted")) ?? null
  }

  /** The frontier: the first not-yet-answered question across the whole plan (null = complete). */
  frontierId(): string | null {
    return this.allBlocks().find((b) => b.gate !== "accepted")?.block.id ?? null
  }

  /** The ONE expanded node — a re-opened answered question wins over the frontier. */
  expandedId(): string | null {
    const reopened = this.reopened.value
    if (reopened && this.allBlocks().some((b) => b.block.id === reopened)) return reopened
    return this.frontierId()
  }

  /** Where a question sits on the stack. Exactly one block is `current`. */
  nodeState(block: BlockView, expandedId: string | null): NodeState {
    if (block.block.id === expandedId) return "current"
    return block.gate === "accepted" ? "done" : "upcoming"
  }

  /** Re-open an answered question for a corrected answer (»ändern«). */
  reopen(blockId: string): void {
    this.reopened.set(blockId)
  }

  /** A round no block of which is generated yet (freshly reached → auto-generate the set). */
  roundUngenerated(round: RoundView): boolean {
    return round.blocks.every((b) => !b.card && b.gate !== "accepted" && !b.skipped)
  }

  async plan(): Promise<void> {
    if (this.busy.value) return
    this.busy.set(true)
    this.error.set(null)
    this.pending.set(null)
    try {
      this.view.set(await apiPost<InterviewView>("/api/workshops/values/interview/plan"))
      this.syncPending()
    } catch (e) {
      // No plan in the view yet → the planner may well have finished without us.
      this.failPending(e, this.view.value?.plan == null)
    } finally {
      this.busy.set(false)
    }
    this.maybeGenerateRound()
  }

  /** Generate a whole round's stimuli in ONE call (parallel probe fan-out server-side). */
  async generateRound(round?: number): Promise<void> {
    if (this.roundGen.value || this.busy.value) return
    this.roundGen.set(true)
    this.error.set(null)
    this.pending.set(null)
    try {
      const res = await apiPost<{ interview: InterviewView }>(
        "/api/workshops/values/interview/round",
        round === undefined ? {} : { round },
      )
      this.view.set(res.interview)
      this.syncPending()
    } catch (e) {
      // Still no cards in the active round → the fan-out may still be running.
      const active = this.currentRound()
      this.failPending(e, active !== null && this.roundUngenerated(active))
    } finally {
      this.roundGen.set(false)
    }
  }

  /** Auto-advance: when the active round is freshly reached (no cards yet), generate the set. */
  maybeGenerateRound(): void {
    const round = this.currentRound()
    if (round && this.roundUngenerated(round) && !this.roundGen.value && !this.busy.value) {
      void this.generateRound(round.round)
    }
  }

  /** Retry ONE block whose probe failed (left card-less inside an already-generated round). */
  async retryBlock(blockId: string): Promise<void> {
    if (this.busy.value || this.roundGen.value) return
    this.roundGen.set(true)
    this.error.set(null)
    try {
      const res = await apiPost<{ interview: InterviewView }>("/api/workshops/values/interview/stimulus", { blockId })
      this.view.set(res.interview)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.roundGen.set(false)
    }
  }

  /** Refuse THIS block's confirm with a reason the candidate reads beside the button. */
  refuseBlock(blockId: string, message: string): void {
    this.blockError.set({ blockId, message })
  }

  async submit(blockId: string, answer: Record<string, unknown>): Promise<void> {
    if (this.busy.value) return
    this.busy.set(true)
    this.error.set(null)
    this.blockError.set(null)
    try {
      const res = await apiPost<{ interview: InterviewView }>("/api/workshops/values/interview/turn", {
        blockId,
        answer: { ...answer, cardId: blockId },
      })
      this.view.set(res.interview)
      this.clearSel(blockId)
      if (this.reopened.value === blockId) this.reopened.set(null)
    } catch (e) {
      // AT the block, not under the page title — the candidate is looking at the button.
      this.refuseBlock(blockId, this.t("interview.card.saveFailed", { message: e instanceof Error ? e.message : String(e) }))
    } finally {
      this.busy.set(false)
    }
    this.maybeGenerateRound() // the round's last block answered → generate the next set
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerValuesInterview extends KitElement<ValuesInterviewVm> {
  protected createViewModel(ctx: KitContext): ValuesInterviewVm {
    return new ValuesInterviewVm(
      ctx.inject(RouterToken),
      ctx.inject(I18nToken),
      ctx.inject(PushToken),
      ctx.inject(WorkshopStoreToken),
      ctx.inject(SessionToken),
    )
  }

  // ── The question STACK: a vertical spine whose nodes ARE the questions ────────
  //    A round is a BATCH (all its cards are generated at once), so the round is a
  //    group heading on the same spine and the questions stack under it. Exactly one
  //    node is expanded (the frontier, or a re-opened answered question); everything
  //    answered collapses to the candidate's own answer, everything ahead is one
  //    quiet line. No panels, no boxes — §0 seamless document.

  /** The answer INPUTS for ONE block (the confirm lives in the node footer). */
  private answerArea(vm: ValuesInterviewVm, block: BlockView, card: ElicitationCard): TemplateResult {
    const id = block.block.id
    // No answerable reaction grid (degraded / card_sort / a free-text primitive) → open text.
    if (isCardDegraded(card) || card.primitive === "card_sort" || card.primitive === "why_ladder" || card.primitive === "open_narrative") {
      return openTextField(vm.t("interview.card.openPlaceholder"), vm.t("interview.card.openPlaceholder"))
    }
    const sel = vm.selFor(id)
    const labels = {
      choose: vm.t("interview.card.choose"),
      best: vm.t("interview.card.best"),
      worst: vm.t("interview.card.worst"),
      veto: vm.t("interview.card.veto"),
      openPlaceholder: vm.t("interview.card.openPlaceholder"),
      whyLabel: vm.t("interview.card.whyLabel"),
      whyPlaceholder: vm.t("interview.card.whyPlaceholder"),
    }
    return renderCardInputs(card as AnswerCard, { id, sel, submitting: vm.busy.value, labels, withWhy: true })
  }

  private submitButton(vm: ValuesInterviewVm, ready: boolean, blockId: string): TemplateResult {
    const submitting = vm.busy.value
    return html`<button
      class="primary iv-submit"
      data-act="submit-answer" data-id=${blockId}
      ?disabled=${submitting || !ready}
      lang="de"
    >${submitting ? vm.t("interview.card.submitting") : vm.t("interview.card.submit")}</button>`
  }

  /** The node footer: the confirm, and the »Warum diese Frage« disclosure the prompt
   *  contract specifies as EXPANDED-ON-DEMAND (never an always-open block of prose). */
  private nodeFooter(vm: ValuesInterviewVm, block: BlockView, ready: boolean): TemplateResult {
    const refusal = vm.blockError.value
    const mine = refusal?.blockId === block.block.id ? refusal.message : null
    return html`<div class="iv-foot">
      ${this.submitButton(vm, ready, block.block.id)}
      ${mine ? html`<p class="iv-refusal" role="alert" lang="de">${mine}</p>` : nothing}
      ${block.denkweise
        ? html`<details class="iv-denkweise">
            <summary lang="de">${vm.t("interview.card.why")}</summary>
            <p lang="de">${block.denkweise}</p>
          </details>`
        : nothing}
    </div>`
  }

  /** The ONE expanded question. `.iv-card` stays the DOM read scope for the textareas
   *  and the slider (see `readTextarea` / `readSlider` / `readWhy`). */
  private currentBody(vm: ValuesInterviewVm, block: BlockView): TemplateResult {
    const card = block.card!
    const id = block.block.id
    const answerable = isCardDegraded(card) || card.primitive === "card_sort" || card.primitive === "why_ladder" || card.primitive === "open_narrative"
      ? true
      : isAnswerable(card.primitive, vm.selFor(id))
    return html`<div class="iv-card" data-block=${id} @keydown=${(e: KeyboardEvent) => this.onCardKeydown(vm, e, id)}>
      ${block.rationale ? html`<p class="iv-rationale" lang="de">${block.rationale}</p>` : nothing}
      ${card.scenario ? html`<p class="iv-scenario" lang="de">${card.scenario}</p>` : nothing}
      <p class="iv-prompt" lang="de">${card.prompt}</p>
      ${this.answerArea(vm, block, card)}
      ${this.nodeFooter(vm, block, answerable)}
    </div>`
  }

  /**
   * The full question of an ANSWERED node, behind the surface's own disclosure idiom
   * (`<details>`, as `.iv-denkweise` already is). A REVISIT, not a re-run: the framing
   * the model gave, the scenario, the prompt, and the option set with the candidate's
   * pick marked — all read-only. »ändern« stays the only way to change an answer.
   */
  private recall(vm: ValuesInterviewVm, block: BlockView): TemplateResult | typeof nothing {
    const card = block.card
    if (!card) return nothing
    const labels = {
      choose: vm.t("interview.card.choose"),
      best: vm.t("interview.card.best"),
      worst: vm.t("interview.card.worst"),
      veto: vm.t("interview.card.veto"),
    }
    return html`<details class="iv-recall">
      <summary lang="de">${vm.t("interview.node.recall")}</summary>
      <div class="iv-recall-body">
        ${block.rationale ? html`<p class="iv-rationale" lang="de">${block.rationale}</p>` : nothing}
        ${card.scenario ? html`<p class="iv-scenario" lang="de">${card.scenario}</p>` : nothing}
        <p class="iv-recall-prompt" lang="de">${card.prompt}</p>
        ${renderCardReview(card as AnswerCard, { review: reviewOf(block), labels })}
        ${block.denkweise
          ? html`<p class="iv-recall-denkweise" lang="de">
              <span class="iv-recall-label" lang="de">${vm.t("interview.card.why")}</span> ${block.denkweise}
            </p>`
          : nothing}
      </div>
    </details>`
  }

  /** An ANSWERED question — its title and the candidate's OWN answer, plus the question
   *  itself one disclosure away, plus »ändern«. */
  private doneBody(vm: ValuesInterviewVm, block: BlockView): TemplateResult {
    // Collapsed it stays a line in a log: the SHORT planned title and what the person
    // said. The full question is REACHABLE (`.iv-recall`) but never in the collapsed flow.
    const title = block.block.title
    const answer = block.skipped ? vm.t("interview.block.skipped") : answerOf(block)
    const why = whyOf(block)
    return html`<div class="iv-done-body">
      <p class="iv-node-title" lang="de">${title}</p>
      ${answer ? html`<p class="iv-answer" lang="de">${answer}</p>` : nothing}
      ${why ? html`<p class="iv-answer-why" lang="de">»${why}«</p>` : nothing}
      ${block.skipped
        ? nothing
        : html`<div class="iv-done-foot">
            ${this.recall(vm, block)}
            <button class="iv-change" data-act="reopen" data-id=${block.block.id} aria-label=${vm.t("interview.node.changeAria", { title })} lang="de">
              ${vm.t("interview.node.change")}
            </button>
          </div>`}
    </div>`
  }

  /** A question still ahead — ONE quiet line (never its prompt: no spoiler, no prose). */
  private upcomingBody(block: BlockView): TemplateResult {
    return html`<p class="iv-node-title" lang="de">${block.block.title}</p>`
  }

  /** A block whose probe failed (card-less within an already-generated round) — a retry. */
  private failedBody(vm: ValuesInterviewVm, block: BlockView): TemplateResult {
    return html`<div role="status">
      <p class="iv-node-title" lang="de">${block.block.title}</p>
      <p class="surface-empty" lang="de">${vm.t("interview.round.failed")}</p>
      <button class=${classMap({ primary: true, "is-pending": vm.roundGen.value })} data-act="retry-block" data-id=${block.block.id} ?disabled=${vm.roundGen.value} lang="de">
        ${vm.roundGen.value ? vm.t("interview.card.generating") : vm.t("interview.card.retry")}
      </button>
    </div>`
  }

  /** The honest »thinking« state while a round's questions are being assembled. */
  private thinkingBody(vm: ValuesInterviewVm, anyAccepted: boolean): TemplateResult {
    return html`<div class="iv-thinking" role="status" aria-live="polite">
      <span class="iv-thinking-dots" aria-hidden="true"><span></span><span></span><span></span></span>
      <span lang="de">${vm.t(anyAccepted ? "interview.round.thinking" : "interview.round.preparing")}</span>
    </div>`
  }

  /** ONE node on the spine: the marker (state) + the state-appropriate body. */
  private node(state: NodeState | "round", body: TemplateResult, extra?: string): TemplateResult {
    return html`<li class=${classMap({ "iv-node": true, [`is-${state}`]: true, ...(extra ? { [extra]: true } : {}) })}>
      <span class="iv-node-marker" aria-hidden="true"></span>
      <div class="iv-node-body">${body}</div>
    </li>`
  }

  /** The whole stack: every round's heading + its question nodes, on one spine. */
  private stack(vm: ValuesInterviewVm, view: InterviewView): TemplateResult {
    const expandedId = vm.expandedId()
    const anyAccepted = view.rounds.some((r) => r.blocks.some((b) => b.gate === "accepted"))
    const nodes: TemplateResult[] = []
    for (const round of view.rounds) {
      const ungenerated = vm.roundUngenerated(round)
      nodes.push(this.node("round", html`<h2 class="iv-round-title eyebrow" lang="de">${round.title}</h2>`))
      for (const block of round.blocks) {
        const state = vm.nodeState(block, expandedId)
        if (state === "current") {
          nodes.push(
            ungenerated
              ? this.node("current", this.thinkingBody(vm, anyAccepted))
              : block.card
                ? this.node("current", this.currentBody(vm, block))
                : this.node("current", this.failedBody(vm, block), "is-failed"),
          )
        } else if (state === "done") {
          nodes.push(this.node("done", this.doneBody(vm, block)))
        } else {
          nodes.push(this.node("upcoming", this.upcomingBody(block)))
        }
      }
    }
    return html`<ol class="iv-stack" role="list">${nodes}</ol>`
  }

  /** The plan was re-cut after a round — say so once, quietly, above the stack. */
  private revisionNote(vm: ValuesInterviewVm, view: InterviewView): TemplateResult | typeof nothing {
    const revision = view.revisions[view.revisions.length - 1]
    if (!revision) return nothing
    return html`<p class="iv-revision" role="status" lang="de">
      <span class="chip is-adjusted" lang="de">${vm.t("interview.rail.adjusted")}</span>
      <span lang="de">${revision.reason}</span>
    </p>`
  }

  // ── States ───────────────────────────────────────────────────────────────────

  private noPyramid(vm: ValuesInterviewVm): TemplateResult {
    return html`<div class="iv-intro">
      <p class="surface-lead" lang="de">${vm.t("interview.intro.needPyramid")}</p>
      <button class="primary" data-act="go-cardgame" lang="de">${vm.t("interview.intro.toCardgame")}</button>
    </div>`
  }

  private readyToPlan(vm: ValuesInterviewVm): TemplateResult {
    return html`<div class="iv-intro">
      <p class="surface-lead" lang="de">${vm.t("interview.intro.lede")}</p>
      <button class=${classMap({ primary: true, "is-pending": vm.busy.value })} data-act="plan" ?disabled=${vm.busy.value} lang="de">
        ${vm.busy.value ? vm.t("interview.intro.planning") : vm.t("interview.intro.plan")}
      </button>
    </div>`
  }

  private body(vm: ValuesInterviewVm, view: InterviewView): TemplateResult {
    if (!view.pyramid) return this.noPyramid(vm)
    if (!view.plan) return this.readyToPlan(vm)

    // ONE column at reading width. The rounds/questions ARE the layout; orientation
    // (»Runde X von Y«) is the shell nav's job and is deliberately absent here.
    return html`
      <div class="iv-layout">
        ${this.revisionNote(vm, view)}
        ${this.stack(vm, view)}
        ${vm.frontierId() === null
          ? html`<div class="iv-done">
              <h2 class="surface-section-title" lang="de">${vm.t("interview.done.title")}</h2>
              <p class="surface-lead" lang="de">${vm.t("interview.done.hint")}</p>
              ${this.reward(vm, view.schwartzProfile)}
              ${this.goldenNext(vm)}
            </div>`
          : nothing}
      </div>`
  }

  /** The completion REWARD (§5c, V4.2): the prognosed Schwartz circumplex wheel + the
   *  model's short German insight (»Das ist dein Bild« upgraded). */
  private reward(vm: ValuesInterviewVm, profile: SchwartzProfile | null): TemplateResult | typeof nothing {
    if (!profile || profile.sectors.length === 0) return nothing
    return html`<div class="iv-reward">
      <p class="eyebrow" lang="de">${vm.t("interview.reward.eyebrow")}</p>
      ${schwartzWheel(profile)}
      ${profile.insight ? html`<p class="iv-reward-insight" lang="de">${profile.insight}</p>` : nothing}
    </div>`
  }

  /** The golden-path forward affordance (D4): the interview is complete here → onward to the Werte-Sektion. */
  private goldenNext(vm: ValuesInterviewVm): TemplateResult | typeof nothing {
    const decl = spineDecl("career-values-interview")
    if (!decl) return nothing
    return nextStep(vm.t("next.eyebrow"), toNextStep(decl, vm.t(decl.labelKey)))
  }

  protected render(vm: ValuesInterviewVm): TemplateResult {
    const rootUrn = (this.urn ?? "urn:career:workshop:values") as Urn
    const err = vm.error.value
    const pending = vm.pending.value
    const view = vm.view.value
    return html`
      <section class="surface values-interview" urn=${rootUrn} aria-labelledby="iv-title">
        <p class="eyebrow" lang="de">${vm.t("interview.kicker")}</p>
        <h1 id="iv-title" class="surface-title" lang="de">${vm.t("surface.valuesInterview.title")}</h1>
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}
        ${pending ? html`<p class="iv-pending" role="status" aria-live="polite" lang="de">${pending}</p>` : nothing}
        ${vm.loaded.value && view ? this.body(vm, view) : nothing}
      </section>`
  }

  protected override actions(vm: ValuesInterviewVm): ActionMap {
    return {
      "go-cardgame": () => vm.go("/workshops/values"),
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
      reopen: ({ id }) => {
        if (id) vm.reopen(id)
      },
      plan: () => void vm.plan(),
      "retry-block": ({ id }) => {
        if (id) void vm.retryBlock(id)
      },
      "pick-option": ({ role, id, block }, event) => {
        if (!role || !id || !block) return
        // A second click on the already-chosen option is the fast-path CONFIRM (operator ruling).
        if (role === "choice" && vm.selFor(block).choiceId === id && event) {
          void this.dispatchAnswer(vm, block, event)
          return
        }
        vm.pickOption(block, role as "choice" | "best" | "worst", id)
      },
      "toggle-veto": ({ id, block }) => {
        if (id && block) vm.toggleVeto(block, id)
      },
      "submit-answer": ({ id }, event) => {
        if (id && event) void this.dispatchAnswer(vm, id, event)
      },
    }
  }

  /** Enter confirms THIS card once it is answerable (fast path); a textarea keeps Enter for
   *  newlines. preventDefault stops a focused cell's native Enter-click from double-firing. */
  private onCardKeydown(vm: ValuesInterviewVm, event: KeyboardEvent, blockId: string): void {
    if (event.key !== "Enter" || event.shiftKey) return
    if ((event.target as HTMLElement).tagName === "TEXTAREA") return
    const block = vm.allBlocks().find((b) => b.block.id === blockId)
    if (!block?.card) return
    if (!isAnswerable(block.card.primitive, vm.selFor(blockId))) return
    event.preventDefault()
    void this.dispatchAnswer(vm, blockId, event)
  }

  /** DOM reads are scoped to the block's own card (a round shows several at once). */
  private readTextarea(event: Event): string {
    return readOpenText((event.target as HTMLElement).closest(".iv-card"))
  }
  private readSlider(event: Event): number {
    return readDifferentialValue((event.target as HTMLElement).closest(".iv-card"))
  }

  /** Build the typed answer for a block (selection from its per-block state, free-text/slider
   *  read from that block's card in the DOM) and fold it. */
  private dispatchAnswer(vm: ValuesInterviewVm, blockId: string, event: Event): void {
    const card = vm.allBlocks().find((b) => b.block.id === blockId)?.card
    if (!card) return
    const sel = vm.selFor(blockId)
    // The mandatory »why« disclosure on a reaction/slider card — the meaning-calibration
    // input (§5, V4.2). Optional to fill; attached to the typed answer when present.
    const why = readWhy((event.target as HTMLElement).closest(".iv-card"))
    const withWhy = (a: Record<string, unknown>): Record<string, unknown> => (why ? { ...a, why } : a)
    let answer: Record<string, unknown> | null = null
    switch (card.primitive) {
      case "pairwise":
        answer = sel.choiceId ? withWhy({ kind: "pairwise", choice: sel.choiceId }) : null
        break
      case "best_worst":
        answer = sel.bestId && sel.worstId ? withWhy({ kind: "best_worst", best: sel.bestId, worst: sel.worstId }) : null
        break
      case "vignette":
      case "simulation":
        answer = sel.choiceId
          ? withWhy({
              kind: "vignette",
              choice: sel.choiceId,
              ...(sel.vetoes.length > 0 ? { vetoes: sel.vetoes.map((v) => ({ id: v, intensity: "niemals" })) } : {}),
            })
          : null
        break
      case "semantic_differential":
        answer = withWhy({ kind: "semantic_differential", position: this.readSlider(event) })
        break
      default: {
        const text = this.readTextarea(event)
        answer = text ? { kind: "open_narrative", text } : null
      }
    }
    // An unbuildable answer used to return in silence, which is the same dead button as a
    // failed save. Say what is missing, at the block.
    if (!answer) {
      vm.refuseBlock(blockId, vm.t("interview.card.needsText"))
      return
    }
    void vm.submit(blockId, answer)
  }
}
